import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Sparkles, ChevronDown, X, TrendingUp, Wallet, Users, Crown, HelpCircle, RefreshCw, WifiOff, RotateCcw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../App';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

// ── IST hour for time-aware greeting ─────────────────────────────────────────
function getISTHour() {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utc + 5.5 * 3600000).getHours();
}
function getTimeGreeting(name) {
  const h = getISTHour();
  if (h >= 5  && h < 12) return `Good morning ${name}.`;
  if (h >= 12 && h < 17) return `Hello ${name}.`;
  if (h >= 17 && h < 21) return `Good evening ${name}.`;
  return `Hello ${name}.`;
}

const AI_ASSISTANT_NAME = 'Invest AI';

// ── Typing animation — renders formatted text in realtime ────────────────────
function TypingText({ text, onDone, speed = 9, isDark }) {
  const [displayed, setDisplayed] = useState('');
  const idxRef = useRef(0);
  const cbRef  = useRef(onDone);
  cbRef.current = onDone;
  const done = displayed.length >= text.length;

  useEffect(() => {
    setDisplayed(''); idxRef.current = 0;
    const actualSpeed = text.length < 60 ? Math.max(speed - 3, 4) : speed;
    const iv = setInterval(() => {
      // Jump by word boundary when possible for smoother rendering of markdown
      const jump = Math.floor(Math.random() * 3) + 1;
      const next = Math.min(idxRef.current + jump, text.length);
      setDisplayed(text.slice(0, next));
      idxRef.current = next;
      if (next >= text.length) { clearInterval(iv); cbRef.current?.(); }
    }, actualSpeed);
    return () => clearInterval(iv);
  }, [text, speed]);

  return (
    <span style={{ display:'block' }}>
      {/* Render partial markdown in realtime — same as final render */}
      <RenderContent text={displayed} isDark={isDark} />
      {!done && (
        <span style={{ display:'inline-block', width:2, height:13, background:'var(--accent)',
          verticalAlign:'middle', marginLeft:1, borderRadius:2, animation:'pulse 1s infinite' }} />
      )}
    </span>
  );
}

// ── Render markdown-lite ──────────────────────────────────────────────────────
function RenderContent({ text, isDark }) {
  const pColor = isDark ? '#e2e8f0' : '#1e1b4b';
  const bold   = isDark ? '#fff'    : '#1e1b4b';
  const code   = isDark ? '#a5b4fc' : '#0d9488';

  return (
    <div style={{ color: pColor }}>
      {text.split('\n').map((line, i) => {
        if (line === '') return <div key={i} style={{ height: 5 }} />;

        // Bullet
        if (line.startsWith('• ') || line.startsWith('- ')) {
          return (
            <div key={i} style={{ display:'flex', gap:7, margin:'2px 0', alignItems:'flex-start' }}>
              <span style={{ color:'var(--accent)', fontWeight:800, flexShrink:0, lineHeight:1.6 }}>•</span>
              <span style={{ lineHeight:1.6 }}>{renderInline(line.slice(2), bold, code)}</span>
            </div>
          );
        }
        // Numbered
        if (/^\d+[️⃣]/.test(line) || /^\d+\. /.test(line)) {
          return <div key={i} style={{ lineHeight:1.6, margin:'2px 0' }}>{renderInline(line, bold, code)}</div>;
        }
        return <div key={i} style={{ lineHeight:1.6, margin:'1px 0' }}>{renderInline(line, bold, code)}</div>;
      })}
    </div>
  );
}

function renderInline(text, bold, code) {
  // Split on **bold** and `code`
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((p, j) => {
    if (p.startsWith('**') && p.endsWith('**'))
      return <strong key={j} style={{ color: bold, fontWeight: 700 }}>{p.slice(2,-2)}</strong>;
    if (p.startsWith('`') && p.endsWith('`'))
      return <code key={j} style={{ color: code, background: 'rgba(20,184,166,0.12)', borderRadius:4, padding:'1px 5px', fontSize:'0.92em', fontFamily:'monospace' }}>{p.slice(1,-1)}</code>;
    return <span key={j}>{p}</span>;
  });
}

// ── Quick action chips ────────────────────────────────────────────────────────
const CHIPS = [
  { icon: <Wallet size={12}/>,     text: 'Mera balance' },
  { icon: <TrendingUp size={12}/>, text: 'Which plan fits my balance?' },
  { icon: <RefreshCw size={12}/>,  text: 'My latest recharge status' },
  { icon: <Users size={12}/>,      text: 'My support ticket status' },
  { icon: <Crown size={12}/>,      text: 'VIP status' },
  { icon: <HelpCircle size={12}/>, text: 'Why is my withdrawal pending?' },
];

function getWelcomeMessage(firstName) {
  return `${getTimeGreeting(firstName)} I'm ${AI_ASSISTANT_NAME}, your Invest Zone assistant.\n\nAsk about balance, investments, withdrawals, referrals, or support.`;
}

// ── Message bubble ────────────────────────────────────────────────────────────
function Message({ msg, isLatest, isDark, onRetry, typingDone, onTypingDone, onAction }) {
  const isUser  = msg.role === 'user';
  const isError = msg.isError;

  return (
    <div style={{ display:'flex', gap:8, flexDirection:isUser?'row-reverse':'row', marginBottom:10, alignItems:'flex-end' }}>
      {!isUser && (
        <div style={{ width:28, height:28, borderRadius:'50%', flexShrink:0,
          ...(isError
            ? { background:'rgba(239,68,68,0.18)', border:'1px solid rgba(239,68,68,0.35)',
                display:'flex', alignItems:'center', justifyContent:'center' }
            : { backgroundImage:'url(/ai-icon.png)', backgroundSize:'185%', backgroundPosition:'center 38%',
                border:'1px solid rgba(13,148,136,0.7)',
                boxShadow:'0 2px 8px rgba(20,184,166,0.35)' }
          ) }}>
          {isError && <Sparkles size={13} color='#f87171' strokeWidth={2.2} />}
        </div>
      )}
      <div style={{ maxWidth:'86%' }}>
        <div style={{
          borderRadius: isUser ? '18px 18px 4px 18px' : '4px 18px 18px 18px',
          fontSize: 13, lineHeight: 1.55, wordBreak: 'break-word',
          padding: '10px 14px',
          background: isUser
            ? 'linear-gradient(135deg,var(--accent),var(--accent-bright))'
            : isError
              ? isDark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.07)'
              : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(20,184,166,0.07)',
          border: isUser ? 'none'
            : isError
              ? '1px solid rgba(239,68,68,0.25)'
              : isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(20,184,166,0.13)',
          color: isUser ? '#ffffff' : isError ? (isDark?'#fca5a5':'#b91c1c') : isDark ? '#e2e8f0' : '#1e1b4b',
          WebkitTextFillColor: isUser ? '#ffffff' : undefined,
        }}>
          {!isUser && isLatest && !typingDone
            ? <TypingText text={msg.content} onDone={onTypingDone} isDark={isDark} />
            : (!isUser
                ? <RenderContent text={msg.content} isDark={isDark} />
                : <span>{msg.content}</span>)
          }
        </div>
        {/* Retry button for errors */}
        {isError && onRetry && (
          <button onClick={onRetry}
            style={{ display:'flex', alignItems:'center', gap:4, marginTop:5, fontSize:11,
              color:'#f87171', background:'none', border:'none', cursor:'pointer', padding:'2px 4px' }}>
            <RotateCcw size={11} /> Try again
          </button>
        )}
        {!isUser && Array.isArray(msg.actions) && msg.actions.length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:7 }}>
            {msg.actions.map((action, idx) => (
              <button
                key={`${action.label}-${idx}`}
                onClick={() => onAction?.(action)}
                style={{
                  padding:'7px 10px',
                  borderRadius:16,
                  border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(20,184,166,0.16)',
                  background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(20,184,166,0.06)',
                  color: isDark ? '#dbeafe' : '#155e75',
                  cursor:'pointer',
                  fontSize:11,
                  fontWeight:600,
                }}>
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AIAura() {
  const { user } = useAuth();
  const { theme }         = useTheme();
  const isDark            = theme === 'dark';
  const firstName         = user?.full_name?.split(' ')[0] || 'there';
  const navigate          = useNavigate();

  const [open,        setOpen]      = useState(false);
  const [hasNew,      setHasNew]    = useState(false);
  const [input,       setInput]     = useState('');
  const [loading,     setLoading]   = useState(false);
  const [isOffline,   setIsOffline] = useState(!navigator.onLine);
  const [convId,      setConvId]    = useState(null);
  const [lastUserMsg, setLastUserMsg] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [recentConversations, setRecentConversations] = useState([]);
  const [loadingConversationId, setLoadingConversationId] = useState(null);

  const [messages, setMessages] = useState(() => [{
    role: 'assistant',
    content: `${getTimeGreeting(firstName)} I'm ${AI_ASSISTANT_NAME}, your Invest Zone assistant.\n\nAsk about balance, investments, withdrawals, referrals, or support.`,
    done: true,
  }]);

  const endRef         = useRef(null);
  const inputRef       = useRef(null);
  const abortRef       = useRef(null);
  const cooldownUntil = useRef(0);
  const lastMsgTime = useRef(0);
  const recentMsgs = useRef([]);
  const pendingAssistantExtra = useRef(null);
  const typingDoneIds  = useRef(new Set()); // track which msgs finished typing (by index)
  const latestAiIdx    = messages.reduce((a, m, i) => m.role === 'assistant' ? i : a, -1);

  // Online/offline detection
  useEffect(() => {
    const goOnline  = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);
    window.addEventListener('online',  goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (open) {
      setTimeout(() => endRef.current?.scrollIntoView({ behavior:'smooth' }), 60);
      if (messages.length <= 1) inputRef.current?.focus();
      setHasNew(false);
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      setHasNew(false);
      // setTimeout ensures DOM is ready + works on mobile
      setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [open]);

  useEffect(() => {
    const body = document.body;
    if (!body) return undefined;
    if (open) body.classList.add('ai-chat-open');
    else body.classList.remove('ai-chat-open');
    return () => body.classList.remove('ai-chat-open');
  }, [open]);

  const addMsg = (role, content, extra = {}) => {
    const mergedExtra = role === 'assistant' && pendingAssistantExtra.current
      ? { ...pendingAssistantExtra.current, ...extra }
      : extra;
    pendingAssistantExtra.current = null;
    setMessages(p => [...p, { role, content, done: role === 'user', ...mergedExtra }]);
  };

  const loadRecentConversations = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/ai/conversations');
      setRecentConversations(Array.isArray(res.data) ? res.data : []);
    } catch {
      setRecentConversations([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadRecentConversations();
  }, [open, loadRecentConversations]);

  useEffect(() => {
    if (open && convId) loadRecentConversations();
  }, [open, convId, loadRecentConversations]);

  const startNewChat = useCallback(() => {
    setConvId(null);
    setShowHistory(false);
    setMessages([{
      role: 'assistant',
    content: getWelcomeMessage(firstName),
      done: true,
    }]);
    typingDoneIds.current = new Set();
  }, [firstName]);

  const openConversation = useCallback(async (conversation) => {
    if (!conversation?.id) return;
    setLoadingConversationId(conversation.id);
    try {
      let sourceMessages = Array.isArray(conversation.messages) ? conversation.messages : null;
      if (!sourceMessages) {
        const res = await api.get(`/ai/conversations/${conversation.id}`);
        sourceMessages = Array.isArray(res.data?.messages) ? res.data.messages : [];
      }
      const loadedMessages = Array.isArray(sourceMessages)
        ? sourceMessages.map(msg => ({ ...msg, done: true }))
        : [];
      if (loadedMessages.length) {
        setMessages(loadedMessages);
        typingDoneIds.current = new Set(loadedMessages.map((_, idx) => idx));
        setConvId(conversation.id);
        setShowHistory(false);
      }
    } catch {
      // keep current chat intact on load failure
    } finally {
      setLoadingConversationId(null);
    }
  }, []);

  const handleAction = useCallback((action) => {
    if (!action) return;
    if (action.type === 'route' && action.route) {
      setOpen(false);
      navigate(action.route);
      return;
    }
    if (action.type === 'prompt' && action.prompt) {
      setInput(action.prompt);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [navigate]);

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    if (isOffline) {
      addMsg('assistant', 'Your internet connection appears to be offline. Check Wi-Fi or mobile data and try again.', { done:true, isError:true });
      return;
    }

    setLastUserMsg(msg);
    addMsg('user', msg);
    setInput('');
    setLoading(true);

    // Cancel previous request if any
    // ── Spam / cooldown check ────────────────────────────────────────────
    const now = Date.now();
    // 1. Cooldown check (set after spam detected)
    if (now < cooldownUntil.current) {
      const secsLeft = Math.ceil((cooldownUntil.current - now) / 1000);
      addMsg('assistant', `⏰ Thoda ruko yaar! ${secsLeft}s mein try karo. Rapid messages se mera server slow hota hai 🙂`, { done:true });
      return;
    }
    // 2. Rate: max 1 msg per 3s
    if (now - lastMsgTime.current < 3000) {
      addMsg('assistant', `Bhai thoda slow down karo! Ek ek karke poochho 😊`, { done:true });
      return;
    }
    lastMsgTime.current = now;

    // 3. Repeat detection - if same message sent 2+ times recently → hardcoded reply
    const recentList = recentMsgs.current;
    const msgNorm    = msg.trim().toLowerCase().slice(0, 50);
    const repeatCount = recentList.filter(m => m === msgNorm).length;
    recentList.push(msgNorm);
    if (recentList.length > 8) recentList.shift();

    if (repeatCount >= 2) {
      cooldownUntil.current = now + 60000; // 1 min cooldown
      addMsg('assistant', `Bro same cheez baar baar pooch rahe ho 😅 Ek minute baad try karo — support chahiye toh Support section mein jao! 🙏`, { done:true });
      return;
    }

    // 4. Rapid spam - if 4+ msgs in last 10 seconds → cooldown
    if (!lastMsgTime._times) lastMsgTime._times = [];
    lastMsgTime._times.push(now);
    lastMsgTime._times = lastMsgTime._times.filter(t => now - t < 10000);
    if (lastMsgTime._times.length > 4) {
      cooldownUntil.current = now + 60000;
      addMsg('assistant', `Yaar bahut fast messages maar rahe ho! 1 minute ka break lo ☕ Phir baat karte hain!`, { done:true });
      return;
    }
    // ─────────────────────────────────────────────────────────────────────

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Timeout: 12 seconds
    const timeout = setTimeout(() => controller.abort(), 12000);

    try {
      const res = await api.post('/ai/chat', { message: msg, conversation_id: convId },
        { signal: controller.signal });
      clearTimeout(timeout);

      if (res.data?.conversation_id) setConvId(res.data.conversation_id);
      const assistantActions = Array.isArray(res.data?.actions) ? res.data.actions : [];
      pendingAssistantExtra.current = assistantActions.length ? { actions: assistantActions } : null;
      addMsg('assistant', res.data.response || 'Something went wrong. Please try again.');
      if (assistantActions.length) {
        setMessages(prev => prev.map((item, idx) => (
          idx === prev.length - 1 ? { ...item, actions: assistantActions } : item
        )));
      }
      if (!open) setHasNew(true);

    } catch (err) {
      clearTimeout(timeout);
      if (err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;

      let errMsg = '';
      if (!navigator.onLine || err.code === 'ERR_NETWORK') {
        errMsg = '📡 Network problem! Internet check karo aur retry karo. 😊';
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errMsg = 'The response is taking longer than expected. Please try again in a moment.';
      } else if (err.response?.status === 500) {
        errMsg = '⚙️ Server thoda busy hai! Ek minute baad try karo. 🙏';
      } else if (err.response?.status === 429) {
        errMsg = 'Too many messages were sent in a short time. Please wait a minute and try again.';
      } else {
        errMsg = '😅 Kuch gadbad hui hai. Retry karo — main ready hoon!';
      }
      addMsg('assistant', errMsg, { done:true, isError:true });
      if (!open) setHasNew(true);
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  }, [input, loading, open, isOffline, convId]);

  const retryLast = useCallback(() => {
    if (lastUserMsg && !loading) {
      // Remove last error message
      setMessages(p => p.filter((m,i) => !(i === p.length-1 && m.isError)));
      send(lastUserMsg);
    }
  }, [lastUserMsg, loading, send]);

  // ── Theme tokens ──────────────────────────────────────────────────────────
  const D           = isDark;
  const panelBg     = D ? 'linear-gradient(180deg,var(--bg-card) 0%,var(--bg-primary) 100%)' : 'linear-gradient(180deg,#f8f9ff 0%,#f0f2fa 100%)';
  const headerBg    = D ? 'rgba(20,184,166,0.07)' : '#ffffff';
  const divider     = D ? 'rgba(255,255,255,0.07)' : 'rgba(20,184,166,0.12)';
  const inputWrapBg = D ? 'rgba(255,255,255,0.05)' : '#ffffff';
  const inputBdr    = D ? 'rgba(255,255,255,0.09)' : 'rgba(20,184,166,0.18)';
  const titleColor  = D ? '#fff' : '#1e1b4b';

  return (
    <div id="ai-aura-root">
      <style>{`
        body.ai-chat-open nav.fixed.bottom-0 {
          display: none !important;
        }
      `}</style>
      {/* ── FAB ── */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale:0 }} animate={{ scale:1 }} exit={{ scale:0 }}
            transition={{ type:'spring', stiffness:300, damping:20 }}
            onClick={() => setOpen(true)}
            className="ai-aura-bubble" style={{ position:'fixed', bottom:88, right:16, zIndex:55,
              width:42, height:42, borderRadius:'50%', cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center',
              backgroundImage:'url(/ai-icon.png)',
              backgroundSize:'165%',
              backgroundPosition:'center 38%',
              border:'1.5px solid rgba(13,148,136,0.85)',
              boxShadow:'0 4px 20px rgba(20,184,166,0.55), 0 0 0 1px rgba(20,184,166,0.2)' }}
            whileHover={{ scale:1.08 }} whileTap={{ scale:0.92 }}>
            {/* Pulse ring — smaller, tighter */}
            <motion.div style={{ position:'absolute', inset:-2, borderRadius:'50%',
              background:'transparent',
              border:'1.5px solid rgba(13,148,136,0.6)', pointerEvents:'none' }}
              animate={{ scale:[1,1.35,1], opacity:[0.6,0,0.6] }}
              transition={{ duration:2.2, repeat:Infinity, ease:'easeInOut' }} />
            {hasNew && <div style={{ position:'absolute', top:-2, right:-2, width:13, height:13,
              background:'#ef4444', borderRadius:'50%', border:`2px solid ${D?'#111':'#fff'}` }} />}
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── Chat Panel ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity:0, y:'100%' }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:'100%' }}
            transition={{ type:'spring', damping:28, stiffness:260 }}
            style={{ position:'fixed', inset:0, zIndex:60, display:'flex' }}>
            <div style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column',
              overflow:'hidden', background:panelBg,
              borderRadius:0,
              border:'none',
              boxShadow: D ? '0 0 50px rgba(20,184,166,0.2)' : '0 0 30px rgba(20,184,166,0.12)' }}>

              {/* ── Header ── */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'14px 18px 12px', flexShrink:0,
                background:headerBg, borderBottom:`1px solid ${divider}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ position:'relative' }}>
                    <div style={{ width:40, height:40, borderRadius:'50%',
                      backgroundImage:'url(/ai-icon.png)', backgroundSize:'185%', backgroundPosition:'center 38%',
                      border:'1.5px solid rgba(13,148,136,0.85)',
                      boxShadow:'0 3px 12px rgba(20,184,166,0.45)' }} />
                    <div style={{ position:'absolute', bottom:-1, right:-1, width:11, height:11,
                      background: isOffline ? '#f59e0b' : '#10b981',
                      borderRadius:'50%', border:`2px solid ${D?'#080812':'#fff'}` }} />
                  </div>
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                      <p style={{ color:titleColor, fontWeight:700, fontSize:14, margin:0 }}>{AI_ASSISTANT_NAME}</p>
                    </div>
                    <p style={{ fontSize:11, margin:'1px 0 0', display:'flex', alignItems:'center', gap:3,
                      color: isOffline ? '#f59e0b' : '#10b981' }}>
                      <span style={{ width:5, height:5, borderRadius:'50%', display:'inline-block',
                        background: isOffline ? '#f59e0b' : '#10b981' }} />
                      {isOffline ? 'Offline' : 'Online'}
                    </p>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <button
                    onClick={startNewChat}
                    style={{
                      padding:'7px 10px',
                      borderRadius:10,
                      background:D?'rgba(255,255,255,0.06)':'rgba(20,184,166,0.07)',
                      border:`1px solid ${divider}`,
                      color:D?'#cbd5e1':'#155e75',
                      cursor:'pointer',
                      fontSize:11,
                      fontWeight:700,
                    }}>
                    New chat
                  </button>
                  <button
                    onClick={() => setShowHistory(v => !v)}
                    style={{
                      width:32, height:32, borderRadius:10, display:'flex', alignItems:'center',
                      justifyContent:'center',
                      background:D?'rgba(255,255,255,0.07)':'rgba(20,184,166,0.08)',
                      border:`1px solid ${divider}`,
                      cursor:'pointer'
                    }}>
                    <ChevronDown size={15} color={D?'#9ca3af':'#0f766e'} />
                  </button>
                  <button onClick={() => setOpen(false)}
                    style={{ width:32, height:32, borderRadius:10, display:'flex', alignItems:'center',
                      justifyContent:'center',
                      background:D?'rgba(255,255,255,0.07)':'rgba(239,68,68,0.08)',
                      border:`1px solid ${D?'rgba(255,255,255,0.08)':'rgba(239,68,68,0.15)'}`,
                      cursor:'pointer' }}>
                    <X size={15} color={D?'#9ca3af':'#ef4444'} />
                  </button>
                </div>
              </div>

              {showHistory && (
                <div style={{
                  padding:'10px 14px',
                  borderBottom:`1px solid ${divider}`,
                  background:D?'rgba(255,255,255,0.03)':'rgba(20,184,166,0.04)',
                  flexShrink:0,
                }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                    <p style={{ margin:0, fontSize:11, fontWeight:700, color:D?'#cbd5e1':'#155e75' }}>Recent chats</p>
                    <button
                      onClick={loadRecentConversations}
                      style={{ border:'none', background:'none', color:D?'#94a3b8':'#0f766e', cursor:'pointer', fontSize:11 }}>
                      Refresh
                    </button>
                  </div>
                  {historyLoading ? (
                    <p style={{ margin:0, fontSize:12, color:D?'#94a3b8':'#64748b' }}>Loading conversations...</p>
                  ) : recentConversations.length === 0 ? (
                    <p style={{ margin:0, fontSize:12, color:D?'#94a3b8':'#64748b' }}>Abhi koi saved conversation nahi hai.</p>
                  ) : (
                    <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:140, overflowY:'auto' }}>
                      {recentConversations.map(conversation => (
                        <button
                          key={conversation.id}
                          onClick={() => openConversation(conversation)}
                          disabled={loadingConversationId === conversation.id}
                          style={{
                            textAlign:'left',
                            borderRadius:12,
                            border:`1px solid ${divider}`,
                            background: convId === conversation.id
                              ? (D ? 'rgba(20,184,166,0.12)' : 'rgba(20,184,166,0.10)')
                              : (D ? 'rgba(255,255,255,0.04)' : '#ffffff'),
                            padding:'8px 10px',
                            cursor:'pointer',
                            color:D?'#e2e8f0':'#1f2937',
                          }}>
                          <div style={{ fontSize:12, fontWeight:600, marginBottom:2 }}>
                            {loadingConversationId === conversation.id ? 'Opening...' : (conversation.first_message || 'Previous conversation')}
                          </div>
                          <div style={{ fontSize:10, color:D?'#94a3b8':'#64748b' }}>
                            {conversation.updated_at ? new Date(conversation.updated_at).toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : ''}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Offline Banner ── */}
              {isOffline && (
                <div style={{ padding:'8px 16px', background:'rgba(245,158,11,0.1)',
                  borderBottom:'1px solid rgba(245,158,11,0.2)',
                  display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                  <WifiOff size={13} color="#f59e0b" />
                  <span style={{ fontSize:12, color:'#f59e0b' }}>No internet connection</span>
                </div>
              )}

              {/* ── Messages ── */}
              <div style={{ flex:1, overflowY:'auto', padding:'14px 14px 6px',
                scrollbarWidth:'thin', scrollbarColor:`${D?'rgba(20,184,166,0.3)':'rgba(20,184,166,0.2)'} transparent` }}>
                {messages.map((m, i) => (
                  <Message key={i} msg={m} isLatest={i===latestAiIdx} isDark={isDark}
                    onRetry={m.isError && i===messages.length-1 ? retryLast : null}
                    typingDone={m.done || typingDoneIds.current.has(i)}
                    onTypingDone={() => typingDoneIds.current.add(i)}
                    onAction={handleAction} />
                ))}

                {/* Typing indicator */}
                {loading && (
                  <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                    <div style={{ width:28, height:28, borderRadius:'50%', flexShrink:0,
                      backgroundImage:'url(/ai-icon.png)', backgroundSize:'185%', backgroundPosition:'center 38%',
                      border:'1px solid rgba(13,148,136,0.7)',
                      boxShadow:'0 2px 8px rgba(20,184,166,0.35)' }} />
                    <div style={{ padding:'10px 14px', borderRadius:'4px 18px 18px 18px',
                      display:'flex', alignItems:'center', gap:5,
                      background:D?'rgba(255,255,255,0.06)':'rgba(20,184,166,0.07)',
                      border:D?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(20,184,166,0.13)' }}>
                      {[0,150,300].map(d => (
                        <motion.div key={d} style={{ width:6, height:6, borderRadius:'50%', background:'var(--accent)' }}
                          animate={{ y:[0,-6,0], opacity:[0.3,1,0.3] }}
                          transition={{ duration:0.9, repeat:Infinity, delay:d/1000 }} />
                      ))}
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {/* ── Quick chips (first visit) ── */}
              {messages.length <= 2 && !loading && (
                <div style={{ padding:'4px 14px 8px', flexShrink:0 }}>
                  <p style={{ fontSize:11, color:D?'#4b5563':'#9ca3af', margin:'0 0 7px' }}>Jaldi poochho:</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {CHIPS.map(c => (
                      <button key={c.text}
                        onClick={() => { setInput(c.text); setTimeout(() => send(c.text), 40); }}
                        style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 11px', fontSize:12,
                          borderRadius:20, cursor:'pointer',
                          border:D?'1px solid rgba(255,255,255,0.08)':'1px solid rgba(20,184,166,0.15)',
                          background:D?'rgba(255,255,255,0.05)':'rgba(20,184,166,0.06)',
                          color:D?'#cbd5e1':'#374151', fontFamily:'inherit' }}>
                        <span style={{ color:'var(--accent)' }}>{c.icon}</span>{c.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Input ── */}
              <div style={{ padding:'10px 14px 14px', flexShrink:0, borderTop:`1px solid ${divider}` }}>
                <div style={{ display:'flex', alignItems:'center', gap:8,
                  padding:'4px 4px 4px 14px', borderRadius:22,
                  background:inputWrapBg, border:`1.5px solid ${inputBdr}`,
                  boxShadow:D?'none':'0 2px 12px rgba(20,184,166,0.08)' }}>
                  <input ref={inputRef} value={input} autoFocus
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={isOffline ? 'Offline...' : 'Ask anything about your account...'}
                    disabled={loading}
                    style={{ flex:1, background:'transparent', color:'var(--text-primary)',
                      fontSize:13, outline:'none', border:'none', fontFamily:'inherit' }} />
                  <motion.button onClick={() => send()} disabled={!input.trim()||loading||isOffline}
                    whileTap={{ scale:0.88 }} whileHover={{ scale: 1.05 }}
                    style={{ width:36, height:36, borderRadius:18, display:'flex', alignItems:'center',
                      justifyContent:'center', flexShrink:0, border:'none',
                      cursor:(input.trim()&&!loading&&!isOffline)?'pointer':'default',
                      background:(input.trim()&&!loading&&!isOffline)
                        ?'linear-gradient(145deg,#0d9488,#9333ea)'
                        :D?'rgba(255,255,255,0.08)':'rgba(20,184,166,0.1)',
                      boxShadow:(input.trim()&&!loading&&!isOffline)
                        ?'0 3px 12px rgba(20,184,166,0.4)':'none',
                      opacity:(!input.trim()||loading||isOffline)?0.4:1,
                      transition:'all 0.2s' }}>
                    <Send size={14} color="white" />
                  </motion.button>
                </div>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}



